import { Check, Info, Pencil, Trash2, Truck, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Control, UseFormSetValue, useWatch } from "react-hook-form";
import { CustomButton } from "../../../../components/ui/button/CustomButton/CustomButton";
import { Badge } from "../../../../components/ui/common/badge";
import { Button } from "../../../../components/ui/common/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../../../components/ui/common/tooltip";
import { FormInput } from "../../../../components/ui/Input/FormInput/FormInput";
import { FormSelect } from "../../../../components/ui/Input/FormSelector/FormSelect";
import { FormUnitInput } from "../../../../components/ui/Input/FormUnitInput/FormUnitInput";
import {  PER_WEIGHT_UNITS_CHEMICAL } from "../../../../config/optionsConstants";
import ChemicalTransport from "./ChemicalTransport";
import { ChemicalSaveOutcome } from "../chemicalContent.types";
import { useChemicalTransportToggle } from "../hooks/useChemicalTransportToggle";

interface ChemicalTypeOption {
  value: string;
  text: string;
}

interface TransportType {
  value: string;
  text: string;
}

interface ChemicalRecordProps {
  index: number;
  fieldPrefix: string;
  isSavedRecord: boolean;
  recordId?: string | number;
  control: Control<any>;
  lockStatus: boolean | undefined;
  chemicalTypeOptions: ChemicalTypeOption[];
  TransportTypeList?: TransportType[];
  handleSaveChemical: (index: number) => Promise<ChemicalSaveOutcome | undefined | void>;
  handleRemoveChemicalRecord: (index: number, id?: string | number) => void;
  processId?: string;
  onChemicalSave?: (processId: string) => void;
  refetchChemical?: () => void;
  setValue: UseFormSetValue<any>;
  type: string;
  supplierDataHandle?: boolean;
  keepExpanded?: boolean;
  onCollapse?: () => void;
}

const ChemicalRecord: React.FC<ChemicalRecordProps> = ({
  index,
  fieldPrefix,
  isSavedRecord,
  recordId,
  control,
  lockStatus,
  chemicalTypeOptions,
  TransportTypeList = [],
  handleSaveChemical,
  handleRemoveChemicalRecord,
  processId,
  onChemicalSave,
  refetchChemical,
  setValue,
  type,
  supplierDataHandle = false,
  keepExpanded = false,
  onCollapse
}) => {
  const [isEditing, setIsEditing] = useState(!isSavedRecord);
  const wasKeepExpanded = useRef(false);

  useEffect(() => {
    if (keepExpanded) {
      setIsEditing(true);
      wasKeepExpanded.current = true;
    } else if (wasKeepExpanded.current) {
      setIsEditing(false);
      wasKeepExpanded.current = false;
    }
  }, [keepExpanded, recordId]);

  const { toggleTransportation } = useChemicalTransportToggle({
    setValue,
    type,
    supplierDataHandle
  });

  const chemicalName = useWatch({ control, name: `${fieldPrefix}.chemicalName` });
  const chemicalType = useWatch({ control, name: `${fieldPrefix}.chemicalType` });
  const amount = useWatch({ control, name: `${fieldPrefix}.amount` });
  const unit = useWatch({ control, name: `${fieldPrefix}.unit` });

  const isEnabled = useWatch({
    control,
    name: `${fieldPrefix}.isTransportationDetailsAvailable`
  });

  const transportation = useWatch({
    control,
    name: `${fieldPrefix}.transportationTypes`
  });

  const selectedChemicalType = useMemo(() => {
    if (!chemicalType || !chemicalTypeOptions) return "—";
    const found = chemicalTypeOptions.find(opt => String(opt.value) === String(chemicalType));
    return found?.text || "—";
  }, [chemicalType, chemicalTypeOptions]);

  const handleSaveAndClose = async (): Promise<void> => {
    const outcome = await handleSaveChemical(index);
    if (outcome === "updated" || (isSavedRecord && outcome !== "created")) {
      setIsEditing(false);
      onCollapse?.();
    }
  };

  const handleCollapse = (): void => {
    setIsEditing(false);
    onCollapse?.();
  };

  const handleToggleTransportation = (enabled: boolean): void => {
    if (!recordId) return;

    toggleTransportation({
      enabled,
      recordId: String(recordId),
      fieldPrefix,
      chemicalName,
      chemicalType,
      amount
    });
  };

  /**
   * Triggers parent refetch to get updated transportation data.
   * The component stays open because we use stable keys based on _id.
   */
  const handleRefetchTransportations = useCallback(
    async (updatedTransportations?: any[]) => {
      if (updatedTransportations !== undefined) {
        setValue(`${fieldPrefix}.transportationTypes`, updatedTransportations);
      } else {
        if (refetchChemical) {
          await refetchChemical();
        }
      }
    },
    [fieldPrefix, setValue, refetchChemical]
  );

  if (isSavedRecord && !isEditing) {
    return (
      <div className="tw-flex tw-items-center tw-gap-2 tw-py-1.5 tw-px-3 tw-rounded-md tw-bg-muted/30 tw-border tw-border-border/50">
        <div className="tw-flex-1 tw-flex tw-items-center tw-gap-3 tw-text-xs">
          <span className="tw-font-semibold tw-text-muted-foreground tw-w-5">{index + 1}</span>
          <span className="tw-font-medium tw-text-foreground">{chemicalName}</span>
          <Badge variant="outline" className="tw-text-[10px] tw-h-5 tw-px-1.5">
            {selectedChemicalType}
          </Badge>
          <span className="tw-font-mono tw-text-muted-foreground tw-ml-auto">
            {amount || 0} {unit}
          </span>
          {isEnabled && transportation && transportation.length > 0 && (
            <Badge variant="secondary" className="tw-text-[10px] tw-h-5 tw-px-1.5">
              <Truck className="tw-h-2.5 tw-w-2.5 tw-mr-1" />
              {transportation.length}
            </Badge>
          )}
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="tw-h-7 tw-w-7 tw-text-muted-foreground hover:tw-text-foreground"
                onClick={() => setIsEditing(true)}
                disabled={lockStatus}
              >
                <Pencil className="tw-h-3 tw-w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="tw-text-xs">Edit</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <Button
          variant="ghost"
          size="icon"
          className="tw-h-7 tw-w-7 tw-text-destructive"
          onClick={() => handleRemoveChemicalRecord(index, recordId)}
          disabled={lockStatus}
        >
          <Trash2 className="tw-h-3 tw-w-3" />
        </Button>
      </div>
    );
  }

  return (
    <div
      className={`tw-border tw-rounded-md tw-p-3 tw-space-y-2 ${
        isSavedRecord
          ? "tw-border-primary/30 tw-bg-primary/5"
          : "tw-border-dashed tw-border-primary/30 tw-bg-primary/5"
      }`}
    >
      {isSavedRecord && (
        <div className="tw-flex tw-items-center tw-justify-between">
          <span className="tw-text-xs tw-font-semibold tw-text-foreground">Chemical {index + 1}</span>
          <Button
            variant="ghost"
            size="icon"
            className="tw-h-7 tw-w-7 tw-text-destructive"
            onClick={() => handleRemoveChemicalRecord(index, recordId)}
            disabled={lockStatus}
          >
            <Trash2 className="tw-h-3 tw-w-3" />
          </Button>
        </div>
      )}

      <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-2">
        <div className="tw-space-y-1">
          <FormInput
            control={control}
            name={`${fieldPrefix}.chemicalName`}
            label="Chemical Name"
            placeholder="e.g., NaOH"
            required={true}
            errorMessage="Chemical Name is required"
            disabled={lockStatus}
          />
        </div>
        <div className="tw-space-y-1">
          <FormSelect
            control={control}
            name={`${fieldPrefix}.chemicalType`}
            label="Chemical"
            options={chemicalTypeOptions}
            disabled={lockStatus}
            required
            placeholder="Select"
          />
        </div>
        <div className="tw-space-y-1">
          <FormUnitInput
            control={control}
            name={`${fieldPrefix}.amount`}
            unitName={`${fieldPrefix}.unit`}
            label="Amount"
            placeholder="0.00"
            required={true}
            errorMessage="Amount is required"
            unitOptions={PER_WEIGHT_UNITS_CHEMICAL}
            disabled={lockStatus}
          />
        </div>
      </div>

      {!isSavedRecord && (
        <p className="tw-flex tw-items-center tw-gap-1.5 tw-text-[10px] tw-text-muted-foreground tw-italic">
          <Info className="tw-h-3 tw-w-3 tw-text-primary" />
          Save this chemical first, then you can add transportation route details.
        </p>
      )}

      {isSavedRecord && (
        <div className="tw-pt-1">
          <span className="tw-text-[10px] tw-text-muted-foreground tw-pt-1">
            Do you know the transportation route?
          </span>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Button
              variant={isEnabled ? "default" : "outline"}
              size="sm"
              className="tw-text-[10px] tw-h-6 tw-px-3"
              onClick={() => handleToggleTransportation(true)}
              disabled={lockStatus}
            >
              Yes, add transport
            </Button>
            <Button
              variant={!isEnabled ? "default" : "outline"}
              size="sm"
              className="tw-text-[10px] tw-h-6 tw-px-3"
              onClick={() => handleToggleTransportation(false)}
              disabled={lockStatus}
            >
              No transport data
            </Button>
          </div>

          {isEnabled && (
            <ChemicalTransport
              lockStatus={lockStatus}
              TransportTypeList={TransportTypeList}
              chemicalId={recordId as string}
              processId={processId}
              type={type}
              supplierDataHandle={supplierDataHandle}
              onChemicalSave={onChemicalSave}
              refetchChemical={refetchChemical}
              transportations={transportation || []}
              refetchTransportations={handleRefetchTransportations}
            />
          )}
        </div>
      )}

      <div className="tw-flex tw-justify-end tw-pt-1 tw-gap-2">
        {isSavedRecord && (
          <CustomButton
            onClick={handleCollapse}
            iconSize="small"
            className="!tw-h-8 tw-rounded-md tw-gap-1.5 tw-px-2 has-[>svg]:tw-px-2.5 !tw-bg-transparent tw-border tw-border-border tw-text-foreground hover:tw-bg-muted/50"
            title={"Cancel"}
            theme="lightgrey"
            iconWithText
            iconChild={<X size={12} />}
          />
        )}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                className="tw-gap-1 tw-text-xs tw-h-8 tw-px-3"
                onClick={handleSaveAndClose}
                disabled={lockStatus}
              >
                <Check className="tw-h-3.5 tw-w-3.5" />
                {isSavedRecord ? "Update Changes" : "Save Record"}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="tw-text-xs">Update changes</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

export default ChemicalRecord;
